import React, { useEffect, useRef, useState } from 'react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  placeholder?: string;
  className?: string;
  disableAutocomplete?: boolean;
}

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter your address",
  className = "",
  disableAutocomplete = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const retryCountRef = useRef(0);

  // Google Places API key - in production, this should be in environment variables
  const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || 'AIzaSyBaP9ei8fRx4NUApJMzG9nZTuZyf3EVjUQ';

  useEffect(() => {
    // If autocomplete is disabled, skip Google Places initialization
    if (disableAutocomplete) {
      setIsLoading(false);
      setError('Address autocomplete is disabled. Please enter your address manually.');
      return;
    }
    
    // Reset retry count
    retryCountRef.current = 0;
    setIsLoading(true);
    
    // Set a timeout to show manual entry option if Google API takes too long
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setError('Google Places API is taking longer than expected. You can enter your address manually.');
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout
    
    // Check if Google Maps API is already loaded
    const checkAndLoadGooglePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) {
        initializeAutocomplete();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
      if (existingScript) {
        // Script is already loading, wait for it
        existingScript.addEventListener('load', () => {
          // Add a small delay to ensure Places library is fully loaded
          setTimeout(() => {
            initializeAutocomplete();
          }, 200);
        });
        return;
      }

      // Load the script only if not already present
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Add a small delay to ensure Places library is fully loaded
        setTimeout(() => {
          initializeAutocomplete();
        }, 200);
      };
      script.onerror = () => {
        setError('Failed to load Google Places API. You can still enter your address manually.');
        setIsLoading(false);
        clearTimeout(timeoutId);
      };
      document.head.appendChild(script);
    };

    const initializeAutocomplete = () => {
      if (!inputRef.current || !window.google) return;

      // Check if Places library is fully loaded
      if (!window.google.maps || !window.google.maps.places || !window.google.maps.places.Autocomplete) {
        if (retryCountRef.current < 10) { // Maximum 10 retries (1 second total)
          console.log(`Places library not fully loaded yet, retrying in 100ms... (attempt ${retryCountRef.current + 1}/10)`);
          retryCountRef.current += 1;
          setTimeout(initializeAutocomplete, 100);
          return;
        } else {
          console.error('Places library failed to load after 10 attempts');
          setError('Google Places API is not available. You can still enter your address manually.');
          setIsLoading(false);
          clearTimeout(timeoutId);
          return;
        }
      }

      try {
        // Create the Autocomplete instance only if no error
        if (!error) {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['address'],
            componentRestrictions: { country: ['us', 'ca'] }, // Restrict to US and Canada
            fields: ['formatted_address', 'address_components', 'geometry']
          });
        }

        if (autocompleteRef.current) {
          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            
            if (place && place.formatted_address) {
              onChange(place.formatted_address);
            } else if (place && (place.name || place.vicinity)) {
              // Try alternative properties
              const address = place.name || place.vicinity;
              if (address && address !== 'undefined') {
                onChange(address);
              }
            }
          });
        }

        setError(null);
        setIsLoading(false);
        clearTimeout(timeoutId);
      } catch (err) {
        console.error('Error initializing Google Places Autocomplete:', err);
        setError('Address autocomplete is not available. You can still enter your address manually.');
        setIsLoading(false);
        clearTimeout(timeoutId);
      }
    };

    checkAndLoadGooglePlaces();

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [GOOGLE_PLACES_API_KEY, onChange, disableAutocomplete, error, isLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };


  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${
          value.trim() === '' ? 'border-gray-300' : 'border-green-300'
        } ${className}`}
        autoComplete="off"
      />
      
      {isLoading && !error && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
        </div>
      )}
      
      {error && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-yellow-800 text-sm font-medium">Address Autocomplete Unavailable</p>
              <p className="text-yellow-700 text-sm mt-1">{error}</p>
              <p className="text-yellow-600 text-xs mt-2">
                You can still type your address manually. Make sure to include your full address including city, state, and ZIP code.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
